import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, Download, RefreshCw } from "lucide-react";
import { API } from "@/lib/apiBase";
import { formatSlots } from "@/lib/formatSlot";

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dy}`;
};

const STATUS_PILL = {
  confirmed: "bg-[#8cc63f] text-black",
  awaiting_confirmation: "bg-yellow-400 text-black",
  pending: "bg-[#a1a1aa] text-black",
  cancelled: "bg-red-500 text-white",
  rejected: "bg-red-500 text-white",
};

const StatCard = ({ label, value, sub, accent }) => (
  <div className="card-surface p-4">
    <p className="label-text mb-1">{label}</p>
    <p
      className={`text-3xl font-black ${
        accent ? "text-[#8cc63f]" : "text-white"
      }`}
    >
      {value}
    </p>
    {sub && (
      <p className="text-[10px] text-[#a1a1aa] mt-1 font-normal">{sub}</p>
    )}
  </div>
);

const DailyReport = () => {
  const [date, setDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/daily-report?date=${d}`
      );
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(date);
  }, [date, fetchReport]);

  const t = report?.totals || {};

  const exportCsv = () => {
    if (!report || !report.bookings?.length) {
      toast.error("Nothing to export");
      return;
    }
    const headers = [
      "Booking ID", "Customer", "Email", "Phone", "Court",
      "Time Slots", "Amount (PHP)", "Status", "Payment",
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = report.bookings.map((b) => [
      b.id, b.customer_name, b.customer_email, b.customer_phone || "",
      b.court_name,
      (b.time_slots || []).join(" | "),
      b.total_amount,
      b.status,
      b.payment_status,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(escape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assenda-daily-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="daily-report-content">
      {/* Header / Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6 print:hidden">
        <div>
          <h1 className="text-4xl font-black tracking-tight">
            Daily <span className="text-[#8cc63f]">Report</span>
          </h1>
          <p className="text-xs text-[#a1a1aa] mt-1">
            Snapshot of bookings, revenue and utilization for a single day.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            data-testid="daily-report-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
          />
          <button
            data-testid="daily-report-today-btn"
            onClick={() => setDate(todayISO())}
            className="px-3 py-2 text-xs uppercase tracking-wider border border-white/10 rounded-sm text-[#a1a1aa] hover:text-white hover:border-[#8cc63f] transition-colors"
          >
            Today
          </button>
          <button
            data-testid="daily-report-refresh"
            onClick={() => fetchReport(date)}
            className="px-3 py-2 text-xs uppercase tracking-wider border border-white/10 rounded-sm text-[#a1a1aa] hover:text-white hover:border-[#8cc63f] transition-colors flex items-center gap-1"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            data-testid="daily-report-print"
            onClick={() => window.print()}
            className="px-3 py-2 text-xs uppercase tracking-wider border border-[#8cc63f] rounded-sm text-[#8cc63f] hover:bg-[#8cc63f] hover:text-black transition-colors flex items-center gap-1"
          >
            <Printer size={12} /> Print
          </button>
          <button
            data-testid="daily-report-export"
            onClick={exportCsv}
            disabled={!report?.bookings?.length}
            className="px-3 py-2 text-xs uppercase tracking-wider border border-[#8cc63f] rounded-sm text-[#8cc63f] hover:bg-[#8cc63f] hover:text-black transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {!loading && report && (
        <>
          {/* Date headline (for print) */}
          <div className="mb-6 hidden print:block">
            <h2 className="text-2xl font-black">
              Assenda Pickleball — Daily Report
            </h2>
            <p className="text-sm">{report.date}</p>
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard
              label="Total Bookings"
              value={t.bookings_total ?? 0}
              sub={`${t.confirmed ?? 0} confirmed`}
            />
            <StatCard
              label="Awaiting"
              value={t.awaiting_confirmation ?? 0}
              sub={`${t.pending ?? 0} pending`}
            />
            <StatCard
              label="Cancelled / Rejected"
              value={(t.cancelled ?? 0) + (t.rejected ?? 0)}
              sub={`${t.cancelled ?? 0}c · ${t.rejected ?? 0}r`}
            />
            <StatCard
              label="Revenue (Confirmed)"
              value={`₱${(t.revenue_confirmed ?? 0).toLocaleString()}`}
              sub={`Potential ₱${(t.revenue_potential ?? 0).toLocaleString()}`}
              accent
            />
            <StatCard
              label="Slots Booked"
              value={`${t.slots_booked ?? 0} / ${t.slots_total ?? 0}`}
              sub={`Across ${t.courts_count ?? 0} courts`}
            />
            <StatCard
              label="Utilization"
              value={`${t.utilization_pct ?? 0}%`}
              sub="confirmed slots ÷ total"
              accent
            />
          </div>

          {/* Per-Court Breakdown */}
          <div className="card-surface p-5 mb-6">
            <h3 className="text-sm uppercase tracking-wider text-[#a1a1aa] mb-3">
              Per-Court Breakdown
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="per-court-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2 label-text">Court</th>
                    <th className="text-right py-2 px-2 label-text">Bookings</th>
                    <th className="text-right py-2 px-2 label-text">Slots Booked</th>
                    <th className="text-right py-2 px-2 label-text">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.per_court || []).map((c) => (
                    <tr
                      key={c.court_id}
                      className="border-b border-white/5"
                      data-testid={`per-court-row-${c.court_id}`}
                    >
                      <td className="py-2 px-2">{c.court_name}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.bookings}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.slots_booked}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold">
                        ₱{(c.revenue || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bookings list */}
          <div className="card-surface p-5">
            <h3 className="text-sm uppercase tracking-wider text-[#a1a1aa] mb-3">
              Bookings on {report.date} ({report.bookings?.length || 0})
            </h3>
            {(!report.bookings || report.bookings.length === 0) ? (
              <p className="text-center py-8 text-[#a1a1aa] text-sm">
                No bookings recorded for this day.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="daily-bookings-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2 label-text">Customer</th>
                      <th className="text-left py-2 px-2 label-text">Court</th>
                      <th className="text-left py-2 px-2 label-text">Slots</th>
                      <th className="text-right py-2 px-2 label-text">Amount</th>
                      <th className="text-left py-2 px-2 label-text">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.bookings.map((b) => (
                      <tr key={b.id} className="border-b border-white/5">
                        <td className="py-2 px-2">
                          <div className="font-semibold">{b.customer_name}</div>
                          <div className="text-[11px] text-[#a1a1aa]">
                            {b.customer_email}
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          {b.court_name}
                          {b.linked_court_name ? ` + ${b.linked_court_name}` : ""}
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {(b.time_slots && b.time_slots.length)
                            ? formatSlots(b.time_slots).join(", ")
                            : "—"}
                        </td>
                        <td className="py-2 px-2 text-right font-semibold tabular-nums">
                          ₱{(b.total_amount || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-2">
                          <span
                            className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                              STATUS_PILL[b.status] || STATUS_PILL.pending
                            }`}
                          >
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailyReport;
